import { useState } from "react";
import { Box } from "@mui/material";

import type {
  AvtFormSection,
  AvtMappingResult,
  Patient,
} from "@thalamos/aspire-common";
import {
  mapSectionsToFields,
  UserSessionEventAction,
} from "@thalamos/aspire-common";

import { useRecordUserSessionEvent } from "../../../hooks/useRecordUserSessionEvent.js";
import { Button } from "../index.js";
import { PopupDialog } from "../PopupDialog/PopupDialog.js";
import { TortusDictationButton } from "./TortusDictationButton.js";
import { TortusDictationPaste } from "./TortusDictationPaste.js";
import { useTortusDictation } from "./TortusDictationProvider.js";
import { TortusDictationReview } from "./TortusDictationReview.js";

export type TortusAvtLaunchBarProps = {
  /** The form's section declaration (e.g. `fdmcaAvtSections`). */
  sections: AvtFormSection[];
  fieldLabels: Record<string, string>;
  currentValues: Record<string, string>;
  /** Show the live dictation launcher (org has tortusAvtDictationEnabled). */
  realEnabled: boolean;
  /** Show the manual dictation paste launcher (org has tortusAvtFakeDictationEnabled). */
  fakeEnabled: boolean;
  patient?: Patient;
  formDraftId?: string;
  onApply: (updates: Record<string, string>) => void;
};

/**
 * Inline launcher at the top of an AVT-enabled form: a "real" button (docks the
 * live Tortus panel) and a "fake" button (paste a note), each gated by an
 * org-config flag. Renders nothing when neither is enabled. Both routes end in
 * the same review → apply step, which distributes one note across the whole form
 * via {@link mapSectionsToFields} rather than filling a single field.
 */
export function TortusAvtLaunchBar({
  sections,
  fieldLabels,
  currentValues,
  realEnabled,
  fakeEnabled,
  patient,
  formDraftId,
  onApply,
}: TortusAvtLaunchBarProps) {
  const { requestDictation } = useTortusDictation();
  const recordEvent = useRecordUserSessionEvent();
  const [fakeOpen, setFakeOpen] = useState(false);
  const [result, setResult] = useState<AvtMappingResult | null>(null);

  if (!realEnabled && !fakeEnabled) {
    return null;
  }

  const applyReviewed = (
    updates: Record<string, string>,
    action: UserSessionEventAction,
  ) => {
    recordEvent(action, formDraftId);
    onApply(updates);
  };

  const dismissReview = () => {
    recordEvent(UserSessionEventAction.avtDiscardNote, formDraftId);
    setResult(null);
  };

  return (
    <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
      {realEnabled && (
        <TortusDictationButton
          testId="tortusLaunchReal"
          onClick={() => {
            requestDictation({
              patient,
              formDraftId,
              skipChooser: true,
              onDictationComplete: (event) => {
                const emitted = event.sections.map((section) => ({
                  title: section.heading,
                  text: section.body,
                }));
                setResult(mapSectionsToFields(emitted, sections));
              },
            });
          }}
        />
      )}
      {fakeEnabled && (
        <Button
          outlined
          small
          color="success"
          label="Paste Dictated Text"
          testId="tortusLaunchFake"
          sx={{
            borderColor: "success.main",
            color: "success.main",
            "&:hover": {
              borderColor: "success.main",
              color: "success.main",
              backgroundColor: "common.paleGreen",
            },
          }}
          onClick={() => {
            setFakeOpen(true);
          }}
        />
      )}

      {result !== null && (
        <PopupDialog open onClose={dismissReview}>
          <TortusDictationReview
            result={result}
            fieldLabels={fieldLabels}
            currentValues={currentValues}
            onApply={(updates) => {
              applyReviewed(updates, UserSessionEventAction.avtUseAiNote);
              setResult(null);
            }}
            onCancel={dismissReview}
          />
        </PopupDialog>
      )}

      {fakeOpen && (
        <PopupDialog
          open
          onClose={() => {
            setFakeOpen(false);
          }}
        >
          <TortusDictationPaste
            sections={sections}
            fieldLabels={fieldLabels}
            currentValues={currentValues}
            onApply={(updates) => {
              applyReviewed(updates, UserSessionEventAction.avtPasteNote);
              setFakeOpen(false);
            }}
            onCancel={() => {
              setFakeOpen(false);
            }}
          />
        </PopupDialog>
      )}
    </Box>
  );
}
